import { forOwn, isArray, isObject, omit } from 'lodash-es';

/**
 * Recursively removes properties from an object.
 *
 * Entries may be:
 *  - A plain name (e.g. `'Depth'`) — stripped wherever it appears.
 *  - A scoped path (e.g. `'TierData.Depth'`) — the final segment is stripped
 *    only from objects whose parent key matches the preceding segment. Useful
 *    when a name collides across types (e.g. `TierData.Depth` vs
 *    `AncillaryPosition.Depth`).
 */
export function removeProps(obj: any, ...propsToRemove: string[]): any {
  const plain = propsToRemove.filter((p) => !p.includes('.'));
  const scoped = propsToRemove
    .filter((p) => p.includes('.'))
    .map((p) => {
      const [parent, ...rest] = p.split('.');
      return { parent, key: rest.join('.') };
    });
  return removeRecursive(obj, plain, scoped, undefined);
}

function removeRecursive(
  obj: any,
  plain: string[],
  scoped: { parent: string; key: string }[],
  parentKey: string | undefined,
): any {
  if (isArray(obj)) {
    return obj.map((item) => removeRecursive(item, plain, scoped, parentKey));
  }
  if (!isObject(obj)) {
    return obj;
  }

  const scopedHere = scoped.filter((s) => s.parent === parentKey).map((s) => s.key);
  const toRemove = [...plain, ...scopedHere];

  const result: any = {};
  forOwn(obj, (value: any, key: string) => {
    result[key] = removeRecursive(value, plain, scoped, key);
  });
  return omit(result, toRemove);
}
